import {
  SEARCH_LICENSE_PRODUCT_IDS,
  SEARCH_LICENSE_PRODUCT_LABELS,
} from '../config/kiddowareUrls';

/**
 * @param {Record<string, unknown> | null | undefined} lic
 */
export function isProductLicenseActive(lic) {
  if (!lic || typeof lic !== 'object' || !lic.product_id) {
    return false;
  }
  const remaining = Number(lic.remaining_days) || 0;
  return (
    lic.is_forever == 1 ||
    lic.is_subscribed == 1 ||
    (lic.isActive == 1 && remaining > 0)
  );
}

/**
 * @param {Record<string, unknown> | null | undefined} subData from subscription/details
 */
export function hasSearchLicense(subData) {
  if (!subData || typeof subData !== 'object') return false;
  return SEARCH_LICENSE_PRODUCT_IDS.some((id) => isProductLicenseActive(subData[id]));
}

/**
 * @param {Record<string, unknown> | null | undefined} lic
 * @param {string} [fallbackName]
 */
export function describeProductLicense(lic, fallbackName = 'License') {
  if (!lic || typeof lic !== 'object' || !lic.product_id) {
    return {
      productId: null,
      name: fallbackName,
      statusLabel: 'No active license',
      active: false,
      tier: 'none',
      expiresLabel: null,
    };
  }

  const name =
    (typeof lic.name === 'string' && lic.name) ||
    SEARCH_LICENSE_PRODUCT_LABELS[lic.product_id] ||
    fallbackName;

  if (lic.is_forever == 1) {
    return {
      productId: lic.product_id,
      name,
      statusLabel: 'Lifetime',
      active: true,
      tier: 'lifetime',
      expiresLabel: lic.expire_at ? `Purchased · ${lic.expire_at}` : null,
    };
  }

  if (lic.is_subscribed == 1) {
    return {
      productId: lic.product_id,
      name,
      statusLabel: 'Active subscription',
      active: true,
      tier: 'paid',
      expiresLabel: lic.expire_at ? `Renews · ${lic.expire_at}` : null,
    };
  }

  const remaining = Number(lic.remaining_days) || 0;
  if (lic.isActive == 1 && remaining > 0) {
    return {
      productId: lic.product_id,
      name,
      statusLabel: `Trial · ${remaining} day${remaining === 1 ? '' : 's'} left`,
      active: true,
      tier: 'trial',
      expiresLabel: lic.expire_at ? `Ends · ${lic.expire_at}` : null,
    };
  }

  return {
    productId: lic.product_id,
    name,
    statusLabel: 'Expired or inactive',
    active: false,
    tier: 'expired',
    expiresLabel: lic.expire_at ? `Ended · ${lic.expire_at}` : null,
  };
}

/**
 * @param {Record<string, unknown> | null | undefined} subData
 */
export function getSearchLicenseSummary(subData) {
  const kw = describeProductLicense(subData?.kw, SEARCH_LICENSE_PRODUCT_LABELS.kw);
  const kpsb = describeProductLicense(subData?.kpsb, SEARCH_LICENSE_PRODUCT_LABELS.kpsb);
  const searchEnabled = hasSearchLicense(subData);

  return {
    searchEnabled,
    kw,
    kpsb,
    headline: searchEnabled
      ? 'Web search is included with your license'
      : 'Upgrade to unlock supervised web search',
  };
}
